import { Prize } from "./Prize";
import { RankingGroup } from "./RankingGroup";
import {
  getSegmentMatchIds,
  getSegmentMatchTitle,
  segmentContainsMatch,
  TournamentSegment,
} from "./Segment/TournamentSegment";
import { TournamentSegmentLink } from "./Segment/TournamentSegmentLink";
import { TournamentTeams } from "./TournamentTeams";
import { Theme } from "../Theme";
import { Links } from "model/Links";
import { TournamentTrack } from "./TournamentTrack";

export interface Tournament {
  description: string | undefined;
  endTimeMs: number;
  filterName: string;
  links: Links | undefined;
  id: string;
  imageIdOverride: string | undefined;
  name: string;
  prizes: Prize[] | undefined;
  rankingGroups: RankingGroup[] | undefined;
  segments: TournamentSegment[];
  segmentLinks: TournamentSegmentLink[] | undefined;
  startTimeMs: number;
  teams: TournamentTeams;
  theme: Theme | undefined;
  tracks: TournamentTrack[];
  trigram: string;
}

export type TournamentStatus = "Upcoming" | "Ongoing" | "Completed";

export const getTournamentMatchIds = (tournament: Tournament) => {
  return tournament.segments.flatMap((s) => getSegmentMatchIds(s) ?? []);
};

export const getTournamentMatchTitle = (
  tournament: Tournament,
  matchId: string,
  includeMatchPart: boolean
) => {
  for (var i = 0; i < tournament.segments.length; i++) {
    const segment = tournament.segments[i];
    if (segmentContainsMatch(segment, matchId)) {
      return getSegmentMatchTitle(segment, matchId, includeMatchPart);
    }
  }

  return undefined;
};

export const getTournamentImageId = (tournament: Tournament) => {
  if (!!tournament.imageIdOverride) {
    return tournament.imageIdOverride;
  }

  return tournament.id;
};

export const getCombinedTournamentSegmentNames = (tournament: Tournament) => {
  const segmentNames: string[] = [];
  const segmentIdsAccountedFor: string[] = [];
  for (let i = 0; i < tournament.segments.length; i++) {
    const { segmentId, name } = tournament.segments[i];
    if (segmentIdsAccountedFor.includes(segmentId)) {
      continue;
    }

    const segmentLink = tournament.segmentLinks?.find((l) =>
      l.segmentIds.includes(segmentId)
    );

    if (!segmentLink) {
      segmentNames.push(name);
      segmentIdsAccountedFor.push(segmentId);
    } else {
      segmentNames.push(segmentLink.name);
      segmentIdsAccountedFor.push(...segmentLink.segmentIds);
    }
  }

  return segmentNames;
};

export const getTournamentSegmentIdsFromName = (
  tournament: Tournament,
  name: string
) => {
  const segmentLink = tournament.segmentLinks?.find((l) => l.name === name);
  if (!!segmentLink) {
    return segmentLink.segmentIds;
  }

  const segmentId = tournament.segments?.find(
    (s) => s.name === name
  )?.segmentId;
  return !!segmentId ? [segmentId] : undefined;
};
